import { Columns3 } from "lucide-react";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";

export interface ColumnSelectorOption {
  /** Column key, must match the data accessor / column definition key. */
  key: string;
  /** Already-translated display label. */
  label: string;
}

export interface ColumnSelectorGroup {
  id: string;
  /** Translated section header. */
  label: string;
  options: ColumnSelectorOption[];
}

interface ColumnSelectorDropdownProps {
  /** Column groups, each rendered under a section header. A group with an empty `label`
   * renders as an unlabeled flat list. */
  groups: ColumnSelectorGroup[];
  /** Keys of columns that are currently hidden. */
  hiddenKeys: string[];
  onToggle: (key: string) => void;
  /** When provided (together with `showAllLabel`), shows an action that un-hides every column. */
  onReset?: () => void;
  /** Translated label for the trigger button and dropdown header. */
  triggerLabel: string;
  /** Translated label for the "show all" reset action. */
  showAllLabel?: string;
}

export function ColumnSelectorDropdown({
  groups,
  hiddenKeys,
  onToggle,
  onReset,
  triggerLabel,
  showAllLabel,
}: ColumnSelectorDropdownProps) {
  const hasHidden = hiddenKeys.length > 0;

  const renderOption = (column: ColumnSelectorOption) => (
    <DropdownMenuCheckboxItem
      key={column.key}
      checked={!hiddenKeys.includes(column.key)}
      onCheckedChange={() => onToggle(column.key)}
      onSelect={(e: Event) => e.preventDefault()}
    >
      {column.label}
    </DropdownMenuCheckboxItem>
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" type="button">
          <Columns3 className="h-4 w-4" />
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-80">
        {onReset && (
          <>
            <div className="flex items-center justify-between gap-2 px-2 py-1">
              <span className="text-sm font-semibold">{triggerLabel}</span>
              {hasHidden && showAllLabel && (
                <button
                  type="button"
                  onClick={onReset}
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showAllLabel}
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {groups.map((group, index) => (
          <div key={group.id}>
            {group.label && (
              <>
                {index > 0 && <DropdownMenuSeparator />}
                <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                  {group.label}
                </div>
              </>
            )}
            {group.options.map(renderOption)}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
